// This module is deliberately maintained as a byte-identical mirror in
// frontend/gateway/src/ai-integration/plcPublicProjection.js and
// frontend/cseditor/src/ai-integration/plcPublicProjection.js (the two bundles share no build
// infrastructure). Each frontend's test suite asserts the copies match — edit
// both files together. Same arrangement as plcCapabilityManifest.js.
//
// Operator-facing projections of the PLC control-plane payloads.
//
// Three endpoints were reaching the AI unprojected — fieldbus readiness, the
// capability manifest, and the edge program/connector inventory. Each is an
// honest internal engineering document, and each is wrong to read aloud to a
// customer:
//
//   * Fieldbus readiness enumerates our release gates. Passing it through told
//     a customer that unreleased protocol backends ship in the binary and that
//     we have no SBOM, no license review, no hardware-lab evidence, no
//     failsafe report and no soak report for them. That is a disclosure about
//     the company, not a fact about their machine.
//
//   * The capability manifest carries internal capability identifiers inside
//     its prose, so whitelisting keys is not enough to keep them out.
//
//   * The program/connector inventory speaks in raw enums and content digests
//     that the I/O page already translates for humans.
//
// The invariant these projections keep: the *safety* meaning survives intact
// and the *internal* vocabulary does not. "EtherCAT cannot run here" must
// still be said plainly, and must still be said before anyone tries; only the
// reason we know it — our own unfinished paperwork — is withheld.
//
// See CLAUDE.md's PLC claim boundary: nothing here may soften the runtime's
// posture. It is pilot-prepared for supervised non-safety use, it is not a
// safety PLC, and a projection that made it sound more capable would be a
// worse bug than the leak it fixed.

import {
  capabilityStatusLabel,
  checkStatusLabel,
  protocolLabel,
  publicSentence,
  runStateLabel,
  syncStateLabel,
} from "./publicVocabulary";

// ── Fieldbus readiness ──────────────────────────────────────────────────

// Readiness values split into two groups with different disclosure rules.
//
// `ready` and `host_blocked` mean the protocol is a shipped feature and the
// only thing between the operator and running it is their own host. Those host
// prerequisites are theirs to act on, so we show them.
//
// `not_implemented` and `release_blocked` mean the protocol is not a product
// yet. Here the checks are ours, not theirs, and every one of them narrates an
// internal gate. We report the single fact that matters — it cannot run — and
// stop. Listing host prerequisites for a feature that does not exist would
// also actively mislead: an operator who "fixed" all of them would be no
// closer to running EtherCAT.
const RELEASED = new Set(["ready", "host_blocked"]);

const prerequisite = (check) => ({
  requirement: publicSentence(check?.label, "A host prerequisite"),
  state: checkStatusLabel(check?.status),
  required: check?.required === true,
  detail: publicSentence(check?.detail, ""),
  next_step: publicSentence(check?.remediation, ""),
});

const protocolReadiness = (entry) => {
  const name = protocolLabel(entry?.protocol);
  const available = entry?.execution_available === true;
  const readiness = String(entry?.readiness || "").trim().toLowerCase();

  if (!RELEASED.has(readiness)) {
    return {
      protocol: name,
      available: false,
      summary: `${name} is not available for production use on this runtime.`,
      // Deliberately absent: prerequisites, backend rows, blocker reasons.
      // Nothing the operator can do changes this answer, and everything that
      // explains it is internal.
      next_step: `Do not plan around ${name} on this runtime. Ask Control Seat about availability before designing it into a project.`,
    };
  }

  const checks = Array.isArray(entry?.checks) ? entry.checks : [];
  const unmet = checks.filter((check) => check?.required === true && check?.status !== "pass");
  return {
    protocol: name,
    available,
    summary: available
      ? `${name} can run on this runtime. Commission it under supervision; this is not a safety controller.`
      : `${name} is supported, but this runtime does not currently meet every prerequisite for it.`,
    prerequisites: checks.map(prerequisite),
    unmet_count: unmet.length,
  };
};

/**
 * publicFieldbusReadiness turns the raw probe manifest into an answer.
 *
 * A malformed or missing manifest yields "unknown", never "available" — the
 * caller must not be able to read silence as a green light.
 */
export const publicFieldbusReadiness = (manifest) => {
  const protocols = Array.isArray(manifest?.protocols) ? manifest.protocols : [];
  const cloud = String(manifest?.target || "") === "cloud_control_plane";
  return {
    scope: cloud
      ? "The cloud service answering this request. It never opens plant network interfaces; ask about a specific enrolled runtime instead."
      : "The runtime host serving this request.",
    protocols: protocols.map(protocolReadiness),
    guidance:
      "Treat a protocol as usable only when `available` is true. A detected network interface, a serial port, or a saved configuration never means the protocol runs.",
  };
};

// ── Capability manifest ─────────────────────────────────────────────────

// Capability prose embeds internal identifiers, so each string is run through
// publicSentence with a fallback derived from the capability's own status.
// That keeps the answer true when the written summary has to be dropped.
const capabilityFallback = (capability) => {
  const name = publicSentence(capability?.name, "This feature");
  switch (String(capability?.status || "").trim().toLowerCase()) {
    case "available":
      return `${name} is available on this host.`;
    case "configuration_only":
      return `${name} can be set up here, but it does not run here. Deploy it to a runtime that can execute it.`;
    default:
      return `${name} is not available yet.`;
  }
};

const publicCapability = (capability) => ({
  name: publicSentence(capability?.name, "Unnamed feature"),
  area: publicSentence(capability?.area, ""),
  status: capabilityStatusLabel(capability?.status),
  executable: capability?.executable === true,
  summary: publicSentence(capability?.summary, capabilityFallback(capability)),
  // Limitations are the most operationally useful field here and the most
  // likely to contain internal identifiers. Drop the individual entries that
  // cannot be shown rather than the whole list, because a partial list of real
  // constraints still protects the operator; then say the list was shortened
  // so the model cannot present it as exhaustive.
  limitations: (Array.isArray(capability?.limitations) ? capability.limitations : [])
    .map((line) => publicSentence(line, ""))
    .filter(Boolean),
  // Deliberately absent: id, depends_on, release_gates, roadmap_stage,
  // location, maturity, validation. Those describe how we build the product,
  // not what the operator can do with it.
});

export const publicCapabilityManifest = (manifest) => {
  const capabilities = Array.isArray(manifest?.capabilities) ? manifest.capabilities : [];
  const withheld = capabilities.reduce((count, capability) => {
    const total = Array.isArray(capability?.limitations) ? capability.limitations.length : 0;
    const kept = publicCapability(capability).limitations.length;
    return count + (total - kept);
  }, 0);
  return {
    scope:
      "The host build serving this request. It does not describe an enrolled remote Pi or IPC — read that runtime's own capabilities before claiming a feature works there.",
    host: {
      operating_system: manifest?.scope?.host_os || "",
      architecture: manifest?.scope?.host_architecture || "",
    },
    capabilities: capabilities.map(publicCapability),
    // Silent truncation would read as "these are all the constraints".
    limitations_withheld: withheld,
    guidance:
      "`executable` true means this build can actually run the feature. Anything else means it cannot, whatever the configuration screens allow you to save.",
  };
};

// ── Edge program inventory ──────────────────────────────────────────────

/**
 * publicEdgeProgram answers the only two questions an operator has about a
 * deployed application: is the device running what we sent, and is it running
 * at all.
 *
 * The raw payload answers those by making the reader compare three pairs of
 * hex digests. We do the comparison here and report the verdict. That is both
 * safer and more reliable — a model asked to eyeball two 64-character strings
 * will eventually say they match when they do not.
 */
const buildIdentityConfirmed = (program) => {
  const desired = program?.desired || {};
  const actual = program?.actual || {};
  if (actual.build_manifest_verified !== true) return false;
  const pairs = [
    [desired.deployment_sha256, actual.deployment_sha256],
    [desired.build_content_sha256, actual.build_content_sha256],
    [desired.build_manifest_sha256, actual.build_manifest_sha256],
  ];
  return pairs.every(([want, got]) => typeof want === "string" && want !== "" && want === got);
};

// The last queued command, in the words the I/O page would use. `kind` and
// `status` are already plain nouns; the error text is not guaranteed to be, so
// it goes through the fail-closed filter like every other free string.
const commandAction = (kind) => {
  switch (String(kind || "").trim()) {
    case "plc.deploy": return "Install or update the application";
    case "plc.set_enabled": return "Change whether the application runs";
    default: return "Change the application";
  }
};

const commandOutcome = (status) => {
  switch (String(status || "").trim().toLowerCase()) {
    case "queued": return "waiting to be sent";
    case "sent": return "sent to the runtime and awaiting confirmation";
    case "applied": return "confirmed by the runtime";
    case "failed": return "failed";
    case "superseded": return "replaced by a newer request";
    case "expired": return "expired before completion";
    default: return "not reported clearly";
  }
};

const publicLastCommand = (command) =>
  command
    ? {
        action: commandAction(command.kind),
        outcome: commandOutcome(command.status),
        problem: command.error ? "The runtime reported that this application change failed." : "",
        sent_at: command.created_at,
      }
    : null;

export const publicEdgeProgram = (program) => {
  const confirmed = buildIdentityConfirmed(program);
  return {
    application: program?.name,
    // A program the device runs that Control Seat did not deploy is a real
    // operational fact, not an implementation detail — keep it.
    managed_by_control_seat: program?.managed === true,
    deployment: syncStateLabel(program?.sync_state),
    // One boolean replacing six digests. False covers both "different build"
    // and "device has not said" — which is correct, because neither is proof.
    application_identity_confirmed: confirmed,
    identity_note: confirmed
      ? "The runtime confirmed that this is the exact application Control Seat sent. Check the reported run state separately."
      : "The runtime has not confirmed that this is the exact application Control Seat sent. Treat its identity as unverified.",
    published_version: program?.desired?.version,
    version_on_device: program?.actual?.version,
    should_be_running: program?.desired?.enabled === true,
    state_on_device: runStateLabel(program?.actual?.runtime?.state),
    problem: program?.actual?.runtime?.error ? "The runtime reported a problem with this application." : "",
    scans: program?.actual?.runtime?.scans,
    overruns: program?.actual?.runtime?.overruns,
    last_command: publicLastCommand(program?.last_command),
  };
};

// ── Edge connector inventory ────────────────────────────────────────────

// The connection kinds a device advertises arrive as internal capability
// identifiers. These are the names the connection wizard shows for them.
//
// EdgeConnectorsModal.js keeps its own copy for the UI. The two are allowed to
// drift because this one fails closed: an identifier missing from the table is
// counted, never named, so a new capability shipped without updating this file
// degrades to "one more kind this device supports" instead of leaking its
// internal name.
const CONNECTION_KIND_NAMES = new Map([
  ["gpio", "Built-in digital I/O"],
  ["modbus-tcp-client", "Modbus TCP"],
  ["modbus-tcp-server", "Modbus TCP server"],
  ["modbus-rtu-client", "Modbus RTU"],
  ["opcua-client", "OPC UA"],
  ["mqtt-direct-topic-v1", "MQTT direct topics"],
  ["enip-class1-scanner", "EtherNet/IP Class 1"],
  ["ethercat-master", "EtherCAT"],
  ["profinet-io-controller", "PROFINET"],
  ["fr202-analog-input-v1", "FR202 analog inputs"],
]);

// Plumbing capabilities. Every device has them, none of them is a connection
// an operator can choose, and listing them only invites the model to recite
// build metadata.
const PLUMBING_CAPABILITIES = new Set([
  "connector-config-v1",
  "edge-tag-catalog-v1",
  "edge-tag-identity-v1",
  "edge-tag-selective-proof-v1",
]);

// Board detection arrives in the same list as connection kinds. It is reported
// separately as `gpio_board`, so counting it here would tell the operator the
// device supports one more connection kind than it does.
const PLUMBING_PREFIXES = ["gpio-board-profile:"];

export const connectorKindNames = (capabilities) => {
  const named = [];
  let unnamed = 0;
  for (const capability of Array.isArray(capabilities) ? capabilities : []) {
    const key = String(capability || "").trim();
    if (!key || PLUMBING_CAPABILITIES.has(key)) continue;
    if (PLUMBING_PREFIXES.some((prefix) => key.startsWith(prefix))) continue;
    const label = CONNECTION_KIND_NAMES.get(key);
    if (label) named.push(label);
    else unnamed += 1;
  }
  return {
    supported: named,
    // Named so the model states the limit instead of presenting a short list
    // as the complete one.
    additional_unlisted: unnamed,
  };
};

export const publicEdgeConnector = (connector) => {
  const applied = connector?.applied || null;
  const server = connector?.kind === "modbus_tcp_server";
  const kindNames = {
    gpio: "Built-in digital I/O",
    modbus: "Modbus device",
    opcua: "OPC UA device",
    mqtt: "MQTT connection",
    "enip-class1": "EtherNet/IP Class 1 device",
    ethercat: "EtherCAT device",
    profinet: "PROFINET device",
    dnp3: "DNP3 device",
    "fr202-analog-input": "FR202 analog input",
    modbus_tcp_server: "Modbus TCP server",
  };
  const fieldDeviceConnection = {
    online: "responding",
    degraded: "responding intermittently",
    offline: "not responding",
    disabled: "turned off",
  }[String(applied?.device_state || "").trim().toLowerCase()];
  return {
    // Kept for tool chaining, never for display. See publicVocabulary.js.
    connector_id: server ? undefined : connector?.connector_id,
    name: connector?.name,
    type: kindNames[connector?.kind] || "Other connection",
    deployment: syncStateLabel(connector?.sync_state),
    should_be: connector?.desired_state === "stopped" ? "turned off" : "turned on",
    // Required only when an AI tool edits this connection. It is hidden
    // transport state and the system prompt forbids displaying it.
    expected_revision: server ? undefined : connector?.desired_revision,
    // The device confirming the exact revision is what "applied" means; the
    // digest behind it is ours.
    device_confirmed:
      applied != null &&
      applied.applied_revision === connector?.desired_revision &&
      typeof applied.applied_sha256 === "string" &&
      applied.applied_sha256 === connector?.desired_sha256,
    state_on_device: runStateLabel(applied?.actual_state),
    field_device_connection: fieldDeviceConnection,
    points_reporting: applied?.point_good,
    points_waiting: applied?.point_degraded,
    problem: applied?.error || connector?.diagnostic
      ? "The runtime reported a problem with this connection."
      : "",
    updated_at: connector?.updated_at,
  };
};
